/* GS In-stock filter — first page view (snippets/facet-filters.liquid, Theme settings >
   Swatches > GS In-stock filter). Rendered only when the store has the Search & Discovery
   Availability filter. The filter form carries a hidden filter.v.availability=1 input, so
   every filter and sort request already sends it; this element covers the page view before
   the first click, whose URL doesn't have it yet. It adds the parameter to the address bar
   (replaceState: no extra history entry) and re-renders the filter panel through the Section
   Rendering API, so the size counts are in-stock counts from the start. The product grid is
   left alone while the result count is unchanged - the in-stock list is a subset of what is
   showing, so an equal count means the same products (fully sold-out products are unlisted),
   and the vendor's loading dim + spinner would otherwise flash on every collection visit. When
   the count differs (e.g. a new zero-stock product that isn't unlisted yet), the vendor's own
   applyFilters re-renders grid + panel. The element sits outside the filter form, so the
   vendor's re-renders never replace it: it runs once. */

if (!customElements.get('gs-facet-in-stock')) {
  class GsFacetInStock extends HTMLElement {
    connectedCallback() {
      const { param } = this.dataset;
      const params = new URLSearchParams(window.location.search);
      if (!param || params.has(param)) return;

      params.set(param, '1');
      this.searchParams = params.toString();
      window.history.replaceState(
        { searchParams: this.searchParams },
        '',
        `${window.location.pathname}?${this.searchParams}`
      );
      // Read back what the browser stored, so later comparisons don't depend on URL encoding.
      this.startHref = window.location.href;

      // Wait for the page to finish loading, so the request never competes with the grid images.
      if (document.readyState === 'complete') {
        this.refreshFilters();
      } else {
        window.addEventListener('load', () => this.refreshFilters(), { once: true });
      }
    }

    /**
     * Re-renders the filter panel with the in-stock parameter applied - the same steps as the
     * vendor's FacetFilters.applyFilters (assets/facet-filters.js), minus the product grid.
     */
    async refreshFilters() {
      const form = document.getElementById('facets');
      const facets = document.querySelector('facet-filters');
      const sectionId = form?.dataset.filterSectionId;
      if (!form || !sectionId || typeof facets?.addListeners !== 'function') return;
      // The customer already filtered or sorted before the page finished loading - that request
      // carried the parameter (hidden input), nothing left to do.
      if (window.location.href !== this.startHref) return;

      // A filter, chip or sort change made before this response arrives sends the parameter too
      // (hidden input), and its own response wins - drop this one.
      let changed = false;
      const markChanged = (evt) => {
        if (evt.type !== 'click' || evt.target.closest('a')) changed = true;
      };
      const sortBy = document.querySelector('.products-toolbar__sort');
      facets.addEventListener('input', markChanged);
      facets.addEventListener('click', markChanged);
      sortBy?.addEventListener('change', markChanged);

      try {
        const response = await fetch(`${window.location.pathname}?${this.searchParams}&section_id=${sectionId}`);
        if (!response.ok) return;

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        const newForm = tmpl.content.getElementById('facets');
        if (changed || !newForm || window.location.href !== this.startHref) return;

        // The mobile "Show N results" button (outside the form) carries the result count on both
        // collection and search pages. A different count means the grid differs too.
        const closeBtn = facets.querySelector('.js-close-drawer-mob');
        const newCloseBtn = tmpl.content.querySelector('.js-close-drawer-mob');
        if (closeBtn && newCloseBtn && closeBtn.textContent.trim() !== newCloseBtn.textContent.trim()) {
          facets.applyFilters(this.searchParams, null, false);
          return;
        }

        // Keep each group open or closed, and "Show more" expanded, as the customer left them.
        form.querySelectorAll('details-disclosure > details').forEach((existing) => {
          const target = tmpl.content.getElementById(existing.id);
          if (target) target.open = existing.open;
        });
        (facets.expanded || []).forEach((id) => {
          const group = tmpl.content.getElementById(id);
          if (!group) return;
          group.querySelectorAll('.js-hidden').forEach((item) => item.classList.remove('js-hidden'));
          group.querySelector('.filter__more')?.remove();
        });

        const activeId = form.contains(document.activeElement) ? document.activeElement.id : '';
        form.innerHTML = newForm.innerHTML;

        // Re-bind the vendor's listeners to the new markup, as its applyFilters does.
        facets.addElements();
        facets.addListeners();

        if (activeId) document.getElementById(activeId)?.focus();
      } catch (error) {
        console.warn(error); // eslint-disable-line
      } finally {
        facets.removeEventListener('input', markChanged);
        facets.removeEventListener('click', markChanged);
        sortBy?.removeEventListener('change', markChanged);
      }
    }
  }

  customElements.define('gs-facet-in-stock', GsFacetInStock);
}
